import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiDelete, apiGet, apiPost, apiPut } from "../../lib/api";
import { useTenantAuth } from "../../context/auth";

type Product = { id: string; name: string; sku: string; branch_id: string; low_stock_threshold: number; is_active?: boolean };
type Location = { id: string; name: string; branch_id: string };

export function ManagerInventoryPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { bootstrap } = useTenantAuth();
  const [branchId, setBranchId] = useState(bootstrap?.branches[0]?.id ?? "");
  const [sku, setSku] = useState("");
  const [name, setName] = useState("");
  const [threshold, setThreshold] = useState(10);
  const [locationName, setLocationName] = useState("");
  const [editingId, setEditingId] = useState("");
  const [editBranchId, setEditBranchId] = useState("");
  const [editSku, setEditSku] = useState("");
  const [editName, setEditName] = useState("");
  const [editThreshold, setEditThreshold] = useState(0);
  const [editActive, setEditActive] = useState(true);

  const products = useQuery({
    queryKey: ["manager-products"],
    queryFn: () => apiGet<{ items: Product[] }>("/v3/inventory/products"),
  });
  const locations = useQuery({
    queryKey: ["manager-locations"],
    queryFn: () => apiGet<{ items: Location[] }>("/v3/inventory/locations"),
  });

  const createProduct = useMutation({
    mutationFn: () =>
      apiPost("/v3/inventory/products", {
        branch_id: branchId,
        sku,
        name,
        unit: "ml",
        low_stock_threshold: threshold,
        is_active: true,
      }),
    onSuccess: async () => {
      setSku("");
      setName("");
      await qc.invalidateQueries({ queryKey: ["manager-products"] });
    },
  });

  const createLocation = useMutation({
    mutationFn: () => apiPost("/v3/inventory/locations", { branch_id: branchId, name: locationName }),
    onSuccess: async () => {
      setLocationName("");
      await qc.invalidateQueries({ queryKey: ["manager-locations"] });
    },
  });

  const updateProduct = useMutation({
    mutationFn: () =>
      apiPut(`/v3/inventory/products/${editingId}`, {
        branch_id: editBranchId,
        sku: editSku,
        name: editName,
        unit: "ml",
        low_stock_threshold: editThreshold,
        is_active: editActive,
      }),
    onSuccess: async () => {
      setEditingId("");
      await qc.invalidateQueries({ queryKey: ["manager-products"] });
    },
  });

  const deleteProduct = useMutation({
    mutationFn: (id: string) => apiDelete(`/v3/inventory/products/${id}`),
    onSuccess: async () => {
      if (editingId) setEditingId("");
      await qc.invalidateQueries({ queryKey: ["manager-products"] });
    },
  });

  const beginEdit = (p: Product) => {
    setEditingId(p.id);
    setEditBranchId(p.branch_id);
    setEditSku(p.sku);
    setEditName(p.name);
    setEditThreshold(p.low_stock_threshold);
    setEditActive(p.is_active ?? true);
  };

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">{t("manager.inventory.title")}</h1>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.inventory.products")}</h2>
        <div className="grid gap-2 mt-2">
          {(products.data?.items ?? []).map((p) => (
            <article key={p.id} className="rounded-lg border border-slate-200 p-3 bg-white">
              {editingId === p.id ? (
                <div className="grid gap-2">
                  <select className="input" value={editBranchId} onChange={(e) => setEditBranchId(e.target.value)}>
                    {(bootstrap?.branches ?? []).map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  </select>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input className="input" value={editSku} onChange={(e) => setEditSku(e.target.value)} />
                    <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </div>
                  <input className="input" type="number" min={0} value={editThreshold} onChange={(e) => setEditThreshold(Number(e.target.value || "0"))} />
                  <label className="text-sm">
                    <input type="checkbox" checked={editActive} onChange={(e) => setEditActive(e.target.checked)} /> {t("common.active")}
                  </label>
                  <div className="flex flex-wrap gap-2">
                    <button className="btn" onClick={() => updateProduct.mutate()} disabled={!editBranchId || !editSku.trim() || !editName.trim()}>
                      {t("common.save")}
                    </button>
                    <button className="btn secondary" onClick={() => setEditingId("")}>
                      {t("common.cancel")}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <p className="font-semibold">{p.name}</p>
                  <p className="text-xs text-slate-500">
                    {t("manager.inventory.skuValue", { sku: p.sku })} | {t("manager.inventory.thresholdValue", { count: p.low_stock_threshold })}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button className="btn secondary" onClick={() => beginEdit(p)}>
                      {t("common.edit")}
                    </button>
                    <button className="btn secondary" onClick={() => deleteProduct.mutate(p.id)}>
                      {t("common.delete")}
                    </button>
                  </div>
                </>
              )}
            </article>
          ))}
        </div>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.inventory.createProduct")}</h2>
        <div className="grid gap-2 mt-2 sm:grid-cols-2 lg:grid-cols-5">
          <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {(bootstrap?.branches ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <input className="input" placeholder={t("manager.inventory.placeholder.sku")} value={sku} onChange={(e) => setSku(e.target.value)} />
          <input className="input" placeholder={t("manager.inventory.placeholder.name")} value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" type="number" min={0} value={threshold} onChange={(e) => setThreshold(Number(e.target.value || "0"))} />
          <button className="btn w-full sm:w-auto" onClick={() => createProduct.mutate()} disabled={!branchId || !sku || !name}>
            {t("manager.inventory.action.addProduct")}
          </button>
        </div>
      </section>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.inventory.stockLocations")}</h2>
        <div className="grid gap-2 mt-2">
          {(locations.data?.items ?? []).map((l) => (
            <article key={l.id} className="rounded-lg border border-slate-200 p-3 bg-white">
              {l.name}
            </article>
          ))}
        </div>
        <div className="grid gap-2 mt-3 sm:grid-cols-2 lg:grid-cols-3">
          <select className="input" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
            {(bootstrap?.branches ?? []).map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
          <input className="input" placeholder={t("manager.inventory.placeholder.locationName")} value={locationName} onChange={(e) => setLocationName(e.target.value)} />
          <button className="btn secondary w-full sm:w-auto" onClick={() => createLocation.mutate()} disabled={!branchId || !locationName.trim()}>
            {t("manager.inventory.action.addLocation")}
          </button>
        </div>
      </section>
    </div>
  );
}

